import React from 'react';
import dayjs from 'dayjs';
import { AlertTriangle, Baby, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '../../../common/DateInput';
import { Input } from '../../../common/Input';
import { Select } from '../../../common/Select';
import { Field } from '../components/Field';
import { SectionHeading } from '../components/SectionHeading';
import {
  ABORTION_MANAGEMENT_OPTIONS,
  DELIVERY_PLACE_OPTIONS,
  DELIVERY_TYPE_OPTIONS,
  IUFD_EXPULSION_OPTIONS,
  MATERNAL_COMPLICATION_OPTIONS,
  NEONATAL_COMPLICATION_OPTIONS,
  NEWBORN_SEX_OPTIONS,
  NEWBORN_VITAL_STATUS_OPTIONS,
  OBSTETRIC_ANOMALY_TYPES,
  PREGNANCY_TERM_STATUS_OPTIONS,
  derivePregnancyTermStatus,
  extractionOptionsForDeliveryType,
  selectCls,
} from '../constants';
import type {
  DeliveryPlaceCode,
  DeliveryTypeCode,
  NewbornSexCode,
  NewbornVitalStatusCode,
  ObstetricAnomalyType,
  PregnancyTermStatusCode,
  PriorDeliveryRowState,
} from '../types';

interface AccouchementTabProps {
  priorDeliveries: PriorDeliveryRowState[];
  errors?: Record<string, string>;
  onAdd: (withAnomaly?: boolean) => void;
  onRemove: (idx: number) => void;
  onUpdate: <K extends keyof PriorDeliveryRowState>(
    idx: number,
    key: K,
    value: PriorDeliveryRowState[K]
  ) => void;
  onToggleMode: (idx: number, withAnomaly: boolean) => void;
  onClearAnomalyError: (idx: number) => void;
}

interface AddButtonsProps {
  onAdd: AccouchementTabProps['onAdd'];
  variant?: 'header' | 'empty-state';
  t: (key: string) => string;
}

const AddButtons: React.FC<AddButtonsProps> = ({ onAdd, variant = 'header', t }) => {
  const isEmpty = variant === 'empty-state';
  return (
    <div className={isEmpty
      ? 'mt-4 flex flex-wrap items-center justify-center gap-2'
      : 'flex flex-wrap items-center gap-2'
    }>
      <button
        type="button"
        onClick={() => onAdd(false)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
      >
        <Plus size={15} />
        {t('dossier_add_delivery_normal')}
      </button>
      <button
        type="button"
        onClick={() => onAdd(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-transparent px-3 py-1.5 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
      >
        <Plus size={15} />
        {t('dossier_add_delivery_anomaly')}
      </button>
    </div>
  );
};

interface ModeSwitchProps {
  withAnomaly: boolean;
  onChange: (next: boolean) => void;
  t: (key: string) => string;
}

const ModeSwitch: React.FC<ModeSwitchProps> = ({ withAnomaly, onChange, t }) => (
  <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium dark:border-gray-700 dark:bg-gray-900/30">
    <button
      type="button"
      onClick={() => onChange(false)}
      className={
        'inline-flex items-center gap-1 rounded-md px-2.5 py-1 transition-colors ' +
        (!withAnomaly
          ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-800 dark:text-blue-300'
          : 'text-slate-600 hover:text-slate-900 dark:text-gray-400 dark:hover:text-gray-200')
      }
    >
      <CheckCircle2 size={12} />
      {t('dossier_mode_no_anomaly')}
    </button>
    <button
      type="button"
      onClick={() => onChange(true)}
      className={
        'inline-flex items-center gap-1 rounded-md px-2.5 py-1 transition-colors ' +
        (withAnomaly
          ? 'bg-white text-amber-700 shadow-sm dark:bg-gray-800 dark:text-amber-300'
          : 'text-slate-600 hover:text-slate-900 dark:text-gray-400 dark:hover:text-gray-200')
      }
    >
      <AlertTriangle size={12} />
      {t('dossier_mode_with_anomaly')}
    </button>
  </div>
);

export const AccouchementTab: React.FC<AccouchementTabProps> = ({
  priorDeliveries,
  errors = {},
  onAdd,
  onRemove,
  onUpdate,
  onToggleMode,
  onClearAnomalyError,
}) => {
  const { t } = useTranslation('Patient');

  const handleToggleMode = (idx: number, withAnomaly: boolean) => {
    onToggleMode(idx, withAnomaly);
    if (!withAnomaly) onClearAnomalyError(idx);
  };

  return (
    <div className="space-y-5">
      <SectionHeading
        icon="🤱"
        title={t('dossier_prior_deliveries_title')}
        description={t('dossier_prior_deliveries_helper')}
        actions={<AddButtons onAdd={onAdd} t={t} />}
      />

      {priorDeliveries.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/60 py-10 text-center dark:border-gray-700 dark:bg-gray-800/30">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-500 dark:bg-blue-900/30">
            <Baby size={22} />
          </div>
          <div className="text-sm font-medium text-title-page-color-100 dark:text-gray-200">
            {t('dossier_no_prior_deliveries_title')}
          </div>
          <div className="mt-1 max-w-xs text-xs text-title-page-color-300 dark:text-gray-400">
            {t('dossier_no_prior_deliveries_helper')}
          </div>
          <AddButtons onAdd={onAdd} variant="empty-state" t={t} />
        </div>
      )}

      <div className="space-y-4">
        {priorDeliveries.map((row, idx) => {
          const anomalyMode = row.has_obstetric_anomalies;
          const anomalyErrorKey = `prior_delivery_${idx}_anomaly_type`;
          return (
            <div
              key={idx}
              className={
                'rounded-xl border bg-white shadow-sm dark:bg-gray-800/40 ' +
                (anomalyMode
                  ? 'border-amber-200 dark:border-amber-800/60'
                  : 'border-slate-200 dark:border-gray-700')
              }
            >
              {/* Header: index + mode badge + mode switch + delete */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <span
                    className={
                      'inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ' +
                      (anomalyMode
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300')
                    }
                  >
                    {idx + 1}
                  </span>
                  <span className="text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
                    {t(
                      anomalyMode
                        ? 'dossier_pregnancy_number'
                        : 'dossier_delivery_number'
                    )}
                  </span>
                  <span
                    className={
                      'ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ' +
                      (anomalyMode
                        ? 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800'
                        : 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800')
                    }
                  >
                    {anomalyMode
                      ? t('dossier_mode_with_anomaly')
                      : t('dossier_mode_no_anomaly')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <ModeSwitch
                    withAnomaly={anomalyMode}
                    onChange={(next) => handleToggleMode(idx, next)}
                    t={t}
                  />
                  <button
                    type="button"
                    onClick={() => onRemove(idx)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={13} />
                    {t('dossier_remove')}
                  </button>
                </div>
              </div>

              {/* Body — branches on the row's mode */}
              <div className="space-y-4 p-4">
                {anomalyMode ? (
                  <AnomalyDeliveryForm
                    idx={idx}
                    row={row}
                    errors={errors}
                    onUpdate={onUpdate}
                    onClearAnomalyError={onClearAnomalyError}
                    anomalyError={errors[anomalyErrorKey]}
                    t={t}
                  />
                ) : (
                  <NormalDeliveryForm
                    idx={idx}
                    row={row}
                    errors={errors}
                    onUpdate={onUpdate}
                    t={t}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const fieldError = (
  errors: Record<string, string> | undefined,
  idx: number,
  field: string
): string | undefined =>
  errors?.[`prior_delivery_${idx}_${field}`] ?? errors?.[field];

export interface NormalDeliveryFormProps {
  idx: number;
  row: PriorDeliveryRowState;
  errors?: Record<string, string>;
  onUpdate: AccouchementTabProps['onUpdate'];
  t: (key: string) => string;
}

export const NormalDeliveryForm: React.FC<NormalDeliveryFormProps> = ({
  idx,
  row,
  errors = {},
  onUpdate,
  t,
}) => {
  const isCesarean = row.delivery_type === 'CESAREAN';
  const isOtherType = row.delivery_type === 'OTHER';
  const extractionOptions = extractionOptionsForDeliveryType(row.delivery_type);
  const showExtractionDropdown = extractionOptions.length > 0;
  const showExtractionFreeText = isOtherType;
  const isStillbirth = row.newborn_vital_status === 'STILLBIRTH';
  const rowError = (field: string) => fieldError(errors, idx, field);

  const handleDeliveryTypeChange = (next: DeliveryTypeCode | '') => {
    onUpdate(idx, 'delivery_type', next);
    const allowed = extractionOptionsForDeliveryType(next).map((o) => o.value);
    if (
      row.extraction_modalities &&
      next !== 'OTHER' &&
      !allowed.includes(row.extraction_modalities as never)
    ) {
      onUpdate(idx, 'extraction_modalities', '');
    }
    if (next !== 'CESAREAN' && row.cesarean_indication) {
      onUpdate(idx, 'cesarean_indication', '');
    }
  };

  const handleVitalStatusChange = (next: NewbornVitalStatusCode | '') => {
    onUpdate(idx, 'newborn_vital_status', next);
    if (next === 'STILLBIRTH' && row.newborn_complication_type) {
      onUpdate(idx, 'newborn_complication_type', '');
    }
  };

  const handleGaChange = (
    field: 'delivery_ga_weeks' | 'delivery_ga_days',
    value: number | null
  ) => {
    onUpdate(idx, field, value);
    const nextWeeks =
      field === 'delivery_ga_weeks' ? value : row.delivery_ga_weeks;
    const nextDays =
      field === 'delivery_ga_days' ? value : row.delivery_ga_days;
    const derived = derivePregnancyTermStatus(nextWeeks, nextDays);
    if (derived && derived !== row.pregnancy_term_status) {
      onUpdate(idx, 'pregnancy_term_status', derived);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Field
        id={`pd-date-${idx}`}
        label={t('dossier_delivery_date')}
        error={rowError('delivery_date')}
      >
        <DateInput
          id={`pd-date-${idx}`}
          name={`pd-date-${idx}`}
          value={row.delivery_date}
          onChange={(e) => onUpdate(idx, 'delivery_date', e.target.value)}
          max={dayjs().format('YYYY-MM-DD')}
        />
      </Field>

      <Field
        id={`pd-delivery-type-${idx}`}
        label={t('dossier_delivery_type')}
        error={rowError('delivery_type')}
      >
        <Select
          id={`pd-delivery-type-${idx}`}
          className={selectCls}
          value={row.delivery_type}
          placeholder={t('dossier_delivery_type_select')}
          onChange={(e) =>
            handleDeliveryTypeChange(e.target.value as DeliveryTypeCode | '')
          }
          options={DELIVERY_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </Field>

      {showExtractionDropdown && (
        <Field
          id={`pd-extraction-${idx}`}
          label={t('dossier_extraction')}
          error={rowError('extraction_modalities')}
        >
          <Select
            id={`pd-extraction-${idx}`}
            className={selectCls}
            value={row.extraction_modalities}
            placeholder={t('dossier_extraction_select')}
            onChange={(e) =>
              onUpdate(idx, 'extraction_modalities', e.target.value)
            }
            options={extractionOptions.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
        </Field>
      )}

      {showExtractionFreeText && (
        <Field
          id={`pd-extraction-${idx}`}
          label={t('dossier_extraction')}
          error={rowError('extraction_modalities')}
        >
          <Input
            id={`pd-extraction-${idx}`}
            placeholder={t('dossier_extraction_placeholder')}
            value={row.extraction_modalities}
            onChange={(e) =>
              onUpdate(idx, 'extraction_modalities', e.target.value)
            }
          />
        </Field>
      )}

      {isCesarean && (
        <Field
          id={`pd-cesarean-${idx}`}
          label={t('dossier_cesarean_indication')}
          error={rowError('cesarean_indication')}
        >
          <Input
            id={`pd-cesarean-${idx}`}
            placeholder={t('dossier_cesarean_indication_placeholder')}
            value={row.cesarean_indication}
            onChange={(e) =>
              onUpdate(idx, 'cesarean_indication', e.target.value)
            }
          />
        </Field>
      )}

      <Field
        id={`pd-ga-weeks-${idx}`}
        label={t('dossier_gestational_age_weeks_days')}
        error={rowError('delivery_ga_weeks') || rowError('delivery_ga_days')}
      >
        <div className="flex items-center gap-2">
          <Input
            id={`pd-ga-weeks-${idx}`}
            type="number"
            min={0}
            max={45}
            placeholder={t('dossier_ga_weeks_placeholder')}
            value={row.delivery_ga_weeks ?? ''}
            onChange={(e) =>
              handleGaChange(
                'delivery_ga_weeks',
                e.target.value === '' ? null : Number(e.target.value)
              )
            }
          />
          <span className="text-xs text-title-page-color-300 dark:text-gray-400">
            {t('dossier_ga_weeks_suffix')}
          </span>
          <Input
            id={`pd-ga-days-${idx}`}
            type="number"
            min={0}
            max={6}
            placeholder={t('dossier_ga_days_placeholder')}
            value={row.delivery_ga_days ?? ''}
            onChange={(e) =>
              handleGaChange(
                'delivery_ga_days',
                e.target.value === '' ? null : Number(e.target.value)
              )
            }
          />
          <span className="text-xs text-title-page-color-300 dark:text-gray-400">
            {t('dossier_ga_days_suffix')}
          </span>
        </div>
      </Field>

      <Field
        id={`pd-term-status-${idx}`}
        label={t('dossier_term_status')}
        hint={t('dossier_term_status_helper')}
        error={rowError('pregnancy_term_status')}
      >
        <Select
          id={`pd-term-status-${idx}`}
          className={selectCls}
          value={row.pregnancy_term_status}
          placeholder={t('dossier_term_status_select')}
          onChange={(e) =>
            onUpdate(
              idx,
              'pregnancy_term_status',
              e.target.value as PregnancyTermStatusCode | ''
            )
          }
          options={PREGNANCY_TERM_STATUS_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </Field>

      <Field
        id={`pd-place-${idx}`}
        label={t('dossier_delivery_place')}
        error={rowError('delivery_place')}
      >
        <Select
          id={`pd-place-${idx}`}
          className={selectCls}
          value={
            DELIVERY_PLACE_OPTIONS.some((o) => o.value === row.delivery_place)
              ? row.delivery_place
              : ''
          }
          placeholder={t('dossier_delivery_place_select')}
          onChange={(e) =>
            onUpdate(
              idx,
              'delivery_place',
              e.target.value as DeliveryPlaceCode | ''
            )
          }
          options={DELIVERY_PLACE_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </Field>

      <Field
        id={`pd-nb-sex-${idx}`}
        label={t('dossier_newborn_sex')}
        error={rowError('newborn_sex')}
      >
        <Select
          id={`pd-nb-sex-${idx}`}
          className={selectCls}
          value={row.newborn_sex}
          placeholder={t('dossier_newborn_sex_select')}
          onChange={(e) =>
            onUpdate(idx, 'newborn_sex', e.target.value as NewbornSexCode | '')
          }
          options={NEWBORN_SEX_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </Field>

      <Field
        id={`pd-nb-vital-${idx}`}
        label={t('dossier_newborn_vital')}
        error={rowError('newborn_vital_status')}
      >
        <Select
          id={`pd-nb-vital-${idx}`}
          className={selectCls}
          value={row.newborn_vital_status}
          placeholder={t('dossier_newborn_vital_select')}
          onChange={(e) =>
            handleVitalStatusChange(
              e.target.value as NewbornVitalStatusCode | ''
            )
          }
          options={NEWBORN_VITAL_STATUS_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </Field>

      <Field
        id={`pd-weight-${idx}`}
        label={t('dossier_newborn_weight')}
        error={rowError('newborn_weight_grams')}
      >
        <Input
          id={`pd-weight-${idx}`}
          type="number"
          min={0}
          max={10000}
          placeholder={t('dossier_newborn_weight_placeholder')}
          value={row.newborn_weight_grams ?? ''}
          onChange={(e) =>
            onUpdate(
              idx,
              'newborn_weight_grams',
              e.target.value === '' ? null : Number(e.target.value)
            )
          }
        />
      </Field>

      <Field
        id={`pd-delivery-comp-${idx}`}
        label={t('dossier_maternal_complications')}
        error={rowError('delivery_complication_type')}
      >
        <Select
          id={`pd-delivery-comp-${idx}`}
          className={selectCls}
          value={row.delivery_complication_type}
          placeholder={t('dossier_maternal_complications_select')}
          onChange={(e) =>
            onUpdate(idx, 'delivery_complication_type', e.target.value)
          }
          options={MATERNAL_COMPLICATION_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </Field>

      {!isStillbirth && (
        <Field
          id={`pd-nb-comp-${idx}`}
          label={t('dossier_neonatal_complications')}
          error={rowError('newborn_complication_type')}
        >
          <Select
            id={`pd-nb-comp-${idx}`}
            className={selectCls}
            value={row.newborn_complication_type}
            placeholder={t('dossier_neonatal_complications_select')}
            onChange={(e) =>
              onUpdate(idx, 'newborn_complication_type', e.target.value)
            }
            options={NEONATAL_COMPLICATION_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
        </Field>
      )}
    </div>
  );
};

export interface AnomalyDeliveryFormProps {
  idx: number;
  row: PriorDeliveryRowState;
  errors?: Record<string, string>;
  onUpdate: AccouchementTabProps['onUpdate'];
  onClearAnomalyError: (idx: number) => void;
  anomalyError?: string;
  t: (key: string) => string;
}

const dateLabelKeyForAnomalyType = (
  type: ObstetricAnomalyType | ''
): string => {
  switch (type) {
    case 'AVORTEMENT':
      return 'dossier_event_date_avortement';
    case 'IUFD':
      return 'dossier_event_date_iufd';
    case 'CONGENITAL':
      return 'dossier_event_date_congenital';
    default:
      return 'dossier_event_date';
  }
};

const secondaryDropdownFor = (
  type: ObstetricAnomalyType | ''
): {
  labelKey: string;
  placeholderKey: string;
  options: { value: string; labelKey: string }[];
} | null => {
  switch (type) {
    case 'AVORTEMENT':
      return {
        labelKey: 'dossier_abortion_mgmt',
        placeholderKey: 'dossier_abortion_mgmt_select',
        options: ABORTION_MANAGEMENT_OPTIONS,
      };
    case 'IUFD':
      return {
        labelKey: 'dossier_iufd_expulsion',
        placeholderKey: 'dossier_iufd_expulsion_select',
        options: IUFD_EXPULSION_OPTIONS,
      };
    case 'CONGENITAL':
      return {
        labelKey: 'dossier_extraction',
        placeholderKey: 'dossier_extraction_select',
        options: [
          { value: 'SPONTANEOUS', labelKey: 'dossier_extraction_spontaneous' },
          { value: 'VACUUM', labelKey: 'dossier_extraction_vacuum' },
          { value: 'FORCEPS', labelKey: 'dossier_extraction_forceps' },
          { value: 'CESAREAN', labelKey: 'dossier_extraction_cesarean' },
          { value: 'BREECH', labelKey: 'dossier_extraction_breech' },
          { value: 'OTHER', labelKey: 'dossier_extraction_other' },
        ],
      };
    default:
      return null;
  }
};

export const AnomalyDeliveryForm: React.FC<AnomalyDeliveryFormProps> = ({
  idx,
  row,
  errors = {},
  onUpdate,
  onClearAnomalyError,
  anomalyError,
  t,
}) => {
  const secondary = secondaryDropdownFor(row.anomaly_type);
  const dateLabelKey = dateLabelKeyForAnomalyType(row.anomaly_type);
  const rowError = (field: string) => fieldError(errors, idx, field);

  const handleAnomalyTypeChange = (next: ObstetricAnomalyType | '') => {
    onUpdate(idx, 'anomaly_type', next);
    if (row.extraction_modalities) {
      onUpdate(idx, 'extraction_modalities', '');
    }
    if (next) onClearAnomalyError(idx);
  };

  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-900/10">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field
          id={`pd-anomaly-type-${idx}`}
          label={t('dossier_anomaly_type')}
          required
          error={anomalyError}
        >
          <Select
            id={`pd-anomaly-type-${idx}`}
            className={selectCls}
            value={row.anomaly_type}
            placeholder={t('dossier_anomaly_type_select')}
            onChange={(e) =>
              handleAnomalyTypeChange(
                e.target.value as ObstetricAnomalyType | ''
              )
            }
            options={OBSTETRIC_ANOMALY_TYPES.map((a) => ({
              value: a.value,
              label: t(a.labelKey),
            }))}
          />
        </Field>

        <Field
          id={`pd-date-${idx}`}
          label={t(dateLabelKey)}
          error={rowError('anomaly_date') || rowError('delivery_date')}
        >
          <DateInput
            id={`pd-date-${idx}`}
            name={`pd-date-${idx}`}
            value={row.delivery_date}
            onChange={(e) => onUpdate(idx, 'delivery_date', e.target.value)}
            max={dayjs().format('YYYY-MM-DD')}
          />
        </Field>

        {secondary && (
          <Field
            id={`pd-secondary-${idx}`}
            label={t(secondary.labelKey)}
            error={rowError('extraction_modalities')}
          >
            <Select
              id={`pd-secondary-${idx}`}
              className={selectCls}
              value={row.extraction_modalities}
              placeholder={t(secondary.placeholderKey)}
              onChange={(e) =>
                onUpdate(idx, 'extraction_modalities', e.target.value)
              }
              options={secondary.options.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
        )}

        <Field
          id={`pd-anomaly-place-${idx}`}
          label={t('dossier_place')}
          error={rowError('anomaly_place')}
        >
          <Input
            id={`pd-anomaly-place-${idx}`}
            placeholder={t('dossier_place_placeholder')}
            value={row.anomaly_place}
            onChange={(e) => onUpdate(idx, 'anomaly_place', e.target.value)}
          />
        </Field>

        <Field
          id={`pd-anomaly-ga-${idx}`}
          label={t('dossier_gestational_age_weeks_days')}
          error={rowError('anomaly_ga_weeks') || rowError('anomaly_ga_days')}
        >
          <div className="flex items-center gap-2">
            <Input
              id={`pd-anomaly-ga-${idx}`}
              type="number"
              min={0}
              max={45}
              placeholder={t('dossier_ga_weeks_placeholder')}
              value={row.anomaly_ga_weeks ?? ''}
              onChange={(e) =>
                onUpdate(
                  idx,
                  'anomaly_ga_weeks',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
            />
            <span className="text-xs text-title-page-color-300 dark:text-gray-400">
              {t('dossier_ga_weeks_suffix')}
            </span>
            <Input
              id={`pd-anomaly-ga-days-${idx}`}
              type="number"
              min={0}
              max={6}
              placeholder={t('dossier_ga_days_placeholder')}
              value={row.anomaly_ga_days ?? ''}
              onChange={(e) =>
                onUpdate(
                  idx,
                  'anomaly_ga_days',
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
            />
            <span className="text-xs text-title-page-color-300 dark:text-gray-400">
              {t('dossier_ga_days_suffix')}
            </span>
          </div>
        </Field>
      </div>

      <Field
        id={`pd-anomaly-desc-${idx}`}
        label={t('dossier_description')}
        error={rowError('anomaly_description')}
      >
        <Input
          id={`pd-anomaly-desc-${idx}`}
          placeholder={t('dossier_description_placeholder')}
          value={row.anomaly_description}
          onChange={(e) => onUpdate(idx, 'anomaly_description', e.target.value)}
        />
      </Field>
    </div>
  );
};
